import styled from "@emotion/styled";
import theme from "../../theme";
import {
  BookMarkIcon,
  GlassIcon,
  NotBookMarkIcon,
  PinIcon,
  RemoveButton,
  XButton,
} from "../../assets/icons/icons";
import { memo, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import userStorage from "../../utils/userStorage";
import useGetBookMark from "../../hooks/useGetBookMark";
import { postBookMark, deleteBookMark } from "../../api/user";
import { css, keyframes } from "@emotion/react";
import { SrLable } from "../../components/common/SrLabel";
import { handleKeyDown } from "../../utils/util";
import { searchNominatim, NominatimSearchResult } from "../../api/nominatim";
import { useI18n } from "../../locales";

const { md, lg } = theme.screen;

export type resultType = {
  address_name: string;
  category_group_code: string;
  category_group_name: string;
  category_name: string;
  distance: string;
  id: string;
  phone: string;
  place_name: string;
  place_url: string;
  road_address_name: string;
  x: string;
  y: string;
};

type Prop = {
  leftFold: boolean;
  isButtonVisible: boolean;
  setIsButtonVisible: React.Dispatch<React.SetStateAction<boolean>>;
  mapContentsToggle: boolean | undefined;
  inputRef: React.RefObject<HTMLInputElement>;
  handleInputFocus: () => void;
  handleBookmark: (
    level: number,
    lat: string | number,
    lng: string | number
  ) => void;
  mapProvider?: "kakao" | "osm";
};

export const AddressSearchBar = memo(
  ({
    leftFold,
    isButtonVisible,
    setIsButtonVisible,
    mapContentsToggle,
    inputRef,
    handleInputFocus,
    handleBookmark,
    mapProvider = "kakao",
  }: Prop) => {
    const { t } = useI18n();
    const user = userStorage.get();
    const queryClient = useQueryClient();
    const [input, setInput] = useState("");
    const [searchResult, setSearchResult] = useState<
      resultType[] | null
    >();
    const [isBookMark, setIsBookMark] = useState(false);

    const { data: bookMarks } = useGetBookMark({
      admin_idx: user?.idx as number,
    });

    useEffect(() => {
      if (!input) return setSearchResult([]);
      
      if (mapProvider === "osm") {
        // OSM: Nominatim 검색 사용
        const searchOSM = async () => {
          try {
            const results = await searchNominatim(input);
            // Nominatim 결과를 카카오 API 형식으로 변환
            const converted: resultType[] = results.map((result: NominatimSearchResult) => ({
              address_name: result.display_name,
              category_group_code: "",
              category_group_name: "",
              category_name: "",
              distance: "",
              id: result.place_id.toString(),
              phone: "",
              place_name: result.display_name.split(",")[0], // 첫 번째 부분을 장소명으로
              place_url: "",
              road_address_name: result.display_name,
              x: result.lon, // 경도
              y: result.lat, // 위도
            }));
            setSearchResult(converted.length > 0 ? converted : null);
          } catch (error) {
            console.error("OSM search error:", error);
            setSearchResult(null);
          }
        };
        searchOSM();
      } else {
        // 카카오맵: 기존 카카오 API 사용
        try {
          const places = new kakao.maps.services.Places();
          const callback = function (result: any, status: any) {
            if (status === kakao.maps.services.Status.OK) {
              setSearchResult(result);
            } else {
              setSearchResult(null);
            }
          };
          places.keywordSearch(input, callback);
        } catch (error) {
          console.error("Kakao search error:", error);
          setSearchResult(null);
        }
      }
    }, [input, mapProvider]);

    return (
      <>
        <InputWrapper
          setFold={leftFold}
          isButtonVisible={isButtonVisible}
          style={
            mapContentsToggle ? { display: "none" } : { display: "block" }
          }
        >
          <SrLable id="address" labelText="장소, 주소를 입력" />
          <SearchInput
            id="address"
            ref={inputRef}
            value={input}
            onFocus={handleInputFocus}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t.control.searchPlace}
          />

          <IconWrapper>
            <GlassIcon />
          </IconWrapper>
          <RemoveButtonWrapper onClick={() => setInput("")}>
            <RemoveButton />
          </RemoveButtonWrapper>
        </InputWrapper>
        {
          <ResultBtnWrapper
            style={
              isButtonVisible
                ? { opacity: "100%" }
                : { opacity: "0%", height: "0px", overflow: "hidden" }
            }
            setFold={leftFold}
          >
            <div>
              <ResultBtn
                isBookMark={isBookMark}
                onClick={() => setIsBookMark(false)}
              >
                검색결과
              </ResultBtn>
              <BookMarkBtn
                isBookMark={isBookMark}
                onClick={() => setIsBookMark(true)}
              >
                즐겨찾기
              </BookMarkBtn>

              {/* 닫기 버튼 */}
              <XButtonContainer onClick={() => setIsButtonVisible(false)}>
                <XButton width="25" height="25" />
              </XButtonContainer>
            </div>
            <BigBox>
              {!isBookMark ? (
                searchResult === null ? (
                  <NoResult>검색결과가 없습니다.</NoResult>
                ) : (
                  searchResult?.map((el) => (
                    <ResultWrapper
                      onClick={(e) => {
                        if (e.target !== e.currentTarget) return;
                        handleBookmark(3, el.y, el.x);
                      }}
                      key={el.id}
                      role="button"
                      tabIndex={0}
                      onKeyDown={handleKeyDown}
                    >
                      <ResultFirst
                        onClick={() => {
                          handleBookmark(3, el.y, el.x);
                          setInput("");
                        }}
                      >
                        <PinIcon />
                        <PlaceName>{el.place_name}</PlaceName>
                      </ResultFirst>
                      <ResultSecond
                        onClick={() => {
                          handleBookmark(3, el.y, el.x);
                          setInput("");
                        }}
                      >
                        <GreenBox>도로명</GreenBox>
                        <PlaceAddress>
                          {el.road_address_name ?? el.address_name}
                        </PlaceAddress>
                      </ResultSecond>
                      {bookMarks
                        ?.map((el) => el.unique_idx)
                        .includes(el.id) ? (
                        <div
                          key={el.id}
                          onClick={async (e) => {
                            e.stopPropagation();
                            const targetId = bookMarks?.find(
                              (bookMark) => bookMark.unique_idx === el.id
                            )?.idx;
                            if (targetId) {
                              await deleteBookMark(targetId as number);
                              queryClient.invalidateQueries({
                                queryKey: ["BookMark"],
                              });
                            }
                          }}
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "30px",
                          }}
                        >
                          <BookMarkIcon />
                        </div>
                      ) : (
                        <div
                          onClick={async () => {
                            const response = await postBookMark({
                              admin_idx: user?.idx as number,
                              unique_idx: el.id,
                              name: el.place_name,
                              latitude: Number(el.y),
                              longitude: Number(el.x),
                              address:
                                el.road_address_name ?? el.address_name,
                            });
                            if (response.code === 2001) {
                              queryClient.invalidateQueries({
                                queryKey: ["BookMark"],
                              });
                            }
                          }}
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "30px",
                          }}
                        >
                          <NotBookMarkIcon />
                        </div>
                      )}
                    </ResultWrapper>
                  ))
                )
              ) : bookMarks?.length === 0 ? (
                <NoResult>등록된 장소/주소가 없습니다.</NoResult>
              ) : (
                bookMarks?.map((el) => (
                  <ResultWrapper
                    onClick={(e) => {
                      if (e.target !== e.currentTarget) return;
                      handleBookmark(3, el.latitude, el.longitude);
                    }}
                    key={el.idx}
                  >
                    <ResultFirst
                      onClick={() => {
                        handleBookmark(3, el.latitude, el.longitude);
                      }}
                    >
                      <PinIcon />
                      <PlaceName>{el.name}</PlaceName>
                    </ResultFirst>
                    <ResultSecond
                      onClick={() => {
                        handleBookmark(3, el.latitude, el.longitude);
                      }}
                    >
                      <GreenBox>도로명</GreenBox>
                      <PlaceAddress>{el.address}</PlaceAddress>
                    </ResultSecond>
                    <div
                      onClick={async (e) => {
                        e.stopPropagation();
                        await deleteBookMark(el.idx as number);
                        queryClient.invalidateQueries({
                          queryKey: ["BookMark"],
                        });
                      }}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "30px",
                      }}
                    >
                      <BookMarkIcon />
                    </div>
                  </ResultWrapper>
                ))
              )}
            </BigBox>
          </ResultBtnWrapper>
        }
      </>
    );
  }
);

const utilBarLeftClose = keyframes`
from{
  left:460px;
}
to{
  left:40px;
}
`;
const utilBarLeftOpen = keyframes`
from{
  left:40px;
}
to{
  left:460px;
}
`;

const XButtonContainer = styled.div`
  background-color: white;
  border-top-right-radius: 5px;
  border-top-left-radius: 5px;
  color: ${theme.color.neutral_400};
  position: absolute;
  z-index: 37;
  width: 36px;
  height: 38px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: ${md}px) {
    right: 5px;
    top: 150px;
  }
  @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    left: 334px;
    top: 181px;
  }
  @media (min-width: ${lg + 1}px) {
    left: 334px;
    top: 181px;
  }
`;

const InputWrapper = styled.div<{
  setFold: boolean;
  isButtonVisible?: boolean;
}>`
  position: absolute;
  z-index: ${({ isButtonVisible }) => (isButtonVisible ? `36` : `3`)};

  @media (max-width: 400px) {
    width: 60%;
    max-width: 263px;
    right: 10px;
    top: 65px;
  }

  @media (min-width: 400px) and (max-width: ${md}px) {
    width: 60%;
    max-width: 263px;
    right: 10px;
    top: 80px;
  }
  @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    left: 460px;
    top: 100px;
    ${({ setFold }) =>
      setFold
        ? css`
            animation: ${utilBarLeftClose} 0.5s ease;
          `
        : !setFold
        ? css`
            animation: ${utilBarLeftOpen} 0.5s ease;
          `
        : null}

    animation-fill-mode:both;
  }
  @media (min-width: ${lg + 1}px) {
    left: 460px;
    top: 100px;
    ${({ setFold }) =>
      setFold
        ? css`
            animation: ${utilBarLeftClose} 0.5s ease;
          `
        : !setFold
        ? css`
            animation: ${utilBarLeftOpen} 0.5s ease;
          `
        : null}

    animation-fill-mode:both;
  }
`;

const SearchInput = styled.input`
  height: 40px;
  background: ${theme.color.white};
  border-radius: 10px;
  position: absolute;
  top: 20px;
  border: none;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.15);
  padding-left: 37px;
  padding-right: 35px;

  @media (max-width: ${md}px) {
    font-size: 12px;
    height: 40px;
    width: 100%;
    /* min-width: 263px; */
  }
  @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    width: 263px;
  }
  @media (min-width: ${lg + 1}px) {
    width: 263px;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  left: 13px;
  top: 32px;
`;
const RemoveButtonWrapper = styled.button`
  position: absolute;
  left: 240px;
  top: 32px;
  cursor: pointer;
  width: 15px;
  height: 15px;

  @media (min-width: 440px) and (max-width: ${md}px) {
    left: 242px;
  }

  @media (max-width: 440px) {
    left: 90%;
  }
`;

const ResultBtnWrapper = styled.div<{ setFold: boolean }>`
  position: absolute;
  top: 0px;

  @media (max-width: ${md}px) {
    height: 60%;
    right: 10px;
    z-index: 35;
    width: 80%;
    max-width: 370px;
  }

  @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    height: 100%;
    left: 460px;
    ${({ setFold }) =>
      setFold
        ? css`
            animation: ${utilBarLeftClose} 0.5s ease;
          `
        : !setFold
        ? css`
            animation: ${utilBarLeftOpen} 0.5s ease;
          `
        : null}
    animation-fill-mode:both;
  }
  @media (min-width: ${lg + 1}px) {
    height: 100%;
    left: 460px;
    ${({ setFold }) =>
      setFold
        ? css`
            animation: ${utilBarLeftClose} 0.5s ease;
          `
        : !setFold
        ? css`
            animation: ${utilBarLeftOpen} 0.5s ease;
          `
        : null}
    animation-fill-mode:both;
  }
`;

const ResultBtn = styled.button<{ isBookMark: boolean }>`
  width: 110px;
  position: absolute;
  z-index: ${({ isBookMark }) => (isBookMark ? 1 : 37)};
  height: 43px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ isBookMark }) =>
    isBookMark ? theme.color.neutral_100 : theme.color.white};
  border-radius: 10px;
  border-bottom-right-radius: 0px;
  border-bottom-left-radius: ${({ isBookMark }) =>
    isBookMark ? "10px" : "0px"};
  font-style: normal;
  font-weight: 500;
  font-size: 15px;
  line-height: 20px;
  text-align: center;
  color: ${({ isBookMark }) =>
    isBookMark ? theme.color.neutral_400 : theme.color.neutral_800};
  box-shadow: ${({ isBookMark }) =>
    isBookMark
      ? "0px 6px 10px rgba(0, 0, 0, 0.25)"
      : "0px 0px 0px  rgba(0, 0, 0, 0.25)"};
  cursor: pointer;

  @media (max-width: ${md}px) {
    top: 150px;
    left: -5px;
  }
  @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    top: 180px;
    left: 0px;
  }
  @media (min-width: ${lg + 1}px) {
    top: 180px;
    left: 0px;
  }
`;

const BookMarkBtn = styled.button<{ isBookMark: boolean }>`
  width: 110px;
  position: absolute;
  z-index: ${({ isBookMark }) => (isBookMark ? 37 : 1)};
  height: 43px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ isBookMark }) =>
    isBookMark ? theme.color.white : theme.color.neutral_100};
  border-radius: 10px;
  border-bottom-left-radius: 0px;
  border-bottom-right-radius: ${({ isBookMark }) =>
    isBookMark ? "0px" : "10px"};
  font-style: normal;
  font-weight: 500;
  font-size: 15px;
  line-height: 20px;
  text-align: center;
  color: ${({ isBookMark }) =>
    isBookMark ? theme.color.neutral_800 : theme.color.neutral_400};
  box-shadow: ${({ isBookMark }) =>
    isBookMark
      ? "0px 0px 0px  rgba(0, 0, 0, 0.25)"
      : "0px 6px 10px rgba(0, 0, 0, 0.25)"};
  cursor: pointer;

  @media (max-width: ${md}px) {
    top: 150px;
    left: 103px;
  }
  @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    top: 180px;
    left: 108px;
  }
  @media (min-width: ${lg + 1}px) {
    top: 180px;
    left: 108px;
  }
`;

const BigBox = styled.div`
  // 결과 박스
  position: absolute;
  z-index: 36;
  background: ${theme.color.white};
  box-shadow: 0px 6px 10px rgba(0, 0, 0, 0.25);
  border-radius: 20px;
  border-top-left-radius: 0px;
  border-top-right-radius: 0px;
  overflow-y: auto;

  @media (max-width: ${md}px) {
    width: 100%;
    height: 228px; // item 하나 높이가 76
    top: 186px;
    right: 5px;
    max-width: 370px;
  }
  @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    width: 370px;
    height: 656px;
    top: 216px;
    left: 0px;
  }
  @media (min-width: ${lg + 1}px) {
    width: 370px;
    height: 656px;
    top: 216px;
    left: 0px;
  }
`;

const ResultWrapper = styled.div`
  padding-left: 15px;
  padding-top: 15px;
  padding-bottom: 15px;
  position: relative;
  cursor: pointer;
  border-bottom: 1px solid ${theme.color.neutral_100};
`;

const ResultFirst = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const ResultSecond = styled.div`
  display: flex;
`;

const PlaceName = styled.p`
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  color: ${theme.color.neutral_800};
`;

const PlaceAddress = styled.p`
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  color: ${theme.color.neutral_800};
`;
const GreenBox = styled.div`
  width: 33px;
  height: 14px;
  font-style: normal;
  font-weight: 700;
  font-size: 8px;
  line-height: 14px;
  display: flex;
  align-items: center;
  text-align: center;
  color: ${theme.color.green_500};
`;

const NoResult = styled.p`
  padding-top: 100px;
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  text-align: center;
  color: ${theme.color.neutral_700};
`;
